import {Component, HostListener, Inject, OnInit} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {
  BaseDialogComponent,
  PermissionsService,
  RefModelStoreService
} from '@q9elements/ui-kit/common';
import {Q9Validators} from '@q9elements/ui-kit/validators';
import {Observable} from 'rxjs';
import {debounceTime, distinctUntilChanged, map} from 'rxjs/operators';

import {CodeSearchDialogData} from '../../models/code-search-grid-columns.model';
import {SEARCH_TYPES} from '../../models/enum/code-search-types.enum';

@Component({
  selector: 'app-run-code-search-dialog',
  templateUrl: 'run-code-search-dialog.component.html',
  styleUrls: ['run-code-search-dialog.component.scss']
})
export class RunCodeSearchDialogComponent extends BaseDialogComponent implements OnInit {
  public searchValueMinLength = 2;
  public searchValueMaxLength = 31;
  public searchTypes = SEARCH_TYPES;
  public validationForm: FormGroup;
  public isFree = this.permissionService.isFree();

  get selectedType() {
    return this.validationForm.get('selectedType');
  }

  get searchValue() {
    return this.validationForm.get('searchValue');
  }

  public get title(): string {
    return this.data.title;
  }

  public get isValid() {
    return (
      !this.selectedType.invalid &&
      ((this.selectedType.value === 'text' && !this.searchValue.invalid) ||
        this.selectedType.value !== 'text')
    );
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) private data: CodeSearchDialogData,
    public dialogRef: MatDialogRef<RunCodeSearchDialogComponent>,
    private readonly rmeStore: RefModelStoreService,
    private readonly permissionService: PermissionsService,
    private router: Router
  ) {
    super();
  }

  ngOnInit() {
    this.initForm();
    this.searchValue.valueChanges
      .pipe(
        this.takeUntilComponentDestroy(),
        debounceTime(400),
        distinctUntilChanged(),
        map((value: string) => value.trim())
      )
      .subscribe();
  }

  @HostListener('document:keydown.enter', ['$event'])
  public onEnterKey(event: KeyboardEvent) {
    event.preventDefault();
    this.onSubmit();
  }

  public noWhitespaceValidator(control: FormControl) {
    return (control.value || '').trim().length >= 2 ? null : {whitespace: true};
  }

  private initForm() {
    this.validationForm = new FormGroup({
      selectedType: new FormControl(null, Q9Validators.required()),
      searchValue: new FormControl(null, [
        Q9Validators.required(),
        Q9Validators.minLength(this.searchValueMinLength),
        Q9Validators.maxLength(this.searchValueMaxLength),
        this.noWhitespaceValidator
      ])
    });
  }

  override onSubmit() {
    if (!this.isValid) {
      this.validationForm.markAllAsTouched();
      this.validationForm.markAsDirty();

      return;
    }

    this.sendSubmitRequest();
  }

  protected override sendSubmitRequest(): Observable<unknown> {
    const payload = {
      ...(this.data.node.id !== this.rmeStore.refModel._id && {rmn: this.data.node.id}),
      type: this.selectedType.value,
      ...(this.searchValue.value && {search: this.searchValue.value})
    };

    const url = this.router.serializeUrl(
      this.router.createUrlTree([`code-search`, this.rmeStore.refModel._id], {
        relativeTo: null,
        queryParams: payload
      })
    );

    window.open(url, '_blank');

    this.dialogRef.close();

    return;
  }
}
